import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { Team, useTeamDestroyMutation } from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import onDestroyListItemCallBack from '@lib/utils/onDestroyListItemCallBack';
import useSearchParam from '@lib/hooks/useSearchParam';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import useTeamsQueryHook from 'utils/fetch/hooks/useTeamsQueryHook';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';

export default function useTeamsActions(props: {
  useBulkSelectResult: UseBulkSelectResult<Team>;
  useModalManagementResult: UseModalManagementResult;
}) {
  const { useBulkSelectResult, useModalManagementResult } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();
  const { selectedSingleTableItem, handleSelectSingleTableItem } =
    useBulkSelectResult;
  const { closeModal, openModal } = useModalManagementResult;

  const selectedTeamId = selectedSingleTableItem?.id || null;
  const outletContext: ContextProps = useOutletContext();
  const currentTeamId = _get(outletContext, 'user.id');

  // Teams
  const teamsQueryHookResult = useTeamsQueryHook({
    searchQuery,
    paginationParams,
    orderingParams: paginationParams,
  });

  const [destroyMutation, destroyMutationResult] = useTeamDestroyMutation({
    onCompleted: () => {
      closeModal(Modals.TeamDestroyModal);
      toast({ content: t('destroy-team-member-success') });
      onDestroyListItemCallBack({
        collection: teamsQueryHookResult.collection,
        navigate,
        pathname,
        refetch: teamsQueryHookResult.refetch,
        search,
        totalPages: teamsQueryHookResult.totalPages,
      });
    },
    onError: (error) => {
      toast({ content: getErrorMessage(error, t) });
    },
  });

  const onCreate = useCallback(() => {
    navigate(APP_URLS.dashboard.teams.add.path);
  }, [navigate]);

  const onView = useCallback(
    (item?: Team) => {
      if (item?.id) {
        navigate(
          APP_URLS.dashboard.teams.view.getDynamicPath({
            pathParts: { id: item.id },
          }),
        );
      }
    },
    [navigate],
  );

  const onEdit = useCallback(
    (item?: Team) => {
      if (item?.id) {
        navigate(
          APP_URLS.dashboard.teams.edit.getDynamicPath({
            pathParts: { id: item.id },
          }),
        );
      }
    },
    [navigate],
  );

  const onDelete = useCallback(
    (item?: Team) => {
      if (item?.id) {
        handleSelectSingleTableItem(item);
        openModal(Modals.TeamDestroyModal);
      }
    },
    [handleSelectSingleTableItem, openModal],
  );

  const onConfirmedDelete = useCallback(() => {
    if (selectedTeamId) {
      destroyMutation({
        variables: {
          id: selectedTeamId,
        },
      });
    }
  }, [destroyMutation, selectedTeamId]);

  return {
    destroyLoading: destroyMutationResult.loading,
    teamsQueryHookResult,
    currentTeamId,
    onConfirmedDelete,
    onCreate,
    onDelete,
    onEdit,
    onView,
    selectedTeamId,
  };
}
